import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const SocialBubbleContainer = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 2000;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    gap: 10px;
  }
`;

const EggButton = styled(motion.button)`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none !important;
  user-select: none;
  filter: drop-shadow(0 8px 18px rgba(0, 0, 0, 0.3));
  transition: filter 0.3s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    filter: drop-shadow(0 12px 24px rgba(243, 156, 18, 0.5));
  }

  &:focus, &:active, &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
`;

const EggSvg = styled.svg`
  width: 76px;
  height: 96px;
  overflow: visible;

  @media (max-width: 768px) {
    width: 64px;
    height: 82px;
  }
`;

const SocialBubbleList = styled(motion.div)`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const SocialBubble = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  color: white;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  border: 2.5px solid #ffffff;
  transition: all 0.3s ease;
  position: relative;
  
  @media (max-width: 768px) {
    width: 46px;
    height: 46px;
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
  
  &.tiktok {
    background: #000000;
  }
  
  &.instagram {
    background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
  }
  
  &.facebook {
    background: #1877f2;
  }
  
  &.twitter {
    background: #000000;
  }
  
  svg {
    width: 30px;
    height: 30px;
    fill: currentColor;
    
    @media (max-width: 768px) {
      width: 25px;
      height: 25px;
    }
  }
`;

const Tooltip = styled(motion.div)`
  position: absolute;
  right: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: 2px solid #ff6b6b;
  border-radius: 15px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  
  &::after {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid #ff6b6b;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
  }
  
  &::before {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid white;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
  }
  
  @media (max-width: 768px) {
    right: 70px;
    font-size: 0.7rem;
    padding: 6px 10px;
  }
`;

const FloatingSocialBubbles = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredBubble, setHoveredBubble] = useState(null);
  const containerRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isOpen]);

  const socialLinks = [
    {
      id: 'tiktok',
      label: 'TikTok',
      url: 'https://www.tiktok.com/@chikin.lady',
      className: 'tiktok',
      svg: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      )
    },
    {
      id: 'instagram',
      label: 'Instagram',
      url: 'https://www.instagram.com/chikin.lady/',
      className: 'instagram',
      svg: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    },
    {
      id: 'facebook',
      label: 'Facebook',
      url: 'https://www.facebook.com/people/Chikin-Lady/61580198363325/',
      className: 'facebook',
      svg: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    {
      id: 'twitter',
      label: 'X (Twitter)',
      url: 'https://x.com/Chikin_Lady',
      className: 'twitter',
      svg: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    }
  ];

  // Play egg crack sound effect (opening only)
  const playEggCrackSound = () => {
    try {
      const audio = new Audio('/assets/egg-crack.mp3');
      audio.volume = 0.95;
      audio.play().catch((err) => {
        console.log('Egg crack audio playback notice:', err);
      });
    } catch (err) {
      console.log('Audio error:', err);
    }
  };

  const handleEggClick = () => {
    // Only play sound when cracking open the egg (no sound when closing/folding)
    if (!isOpen) {
      playEggCrackSound();
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <SocialBubbleContainer ref={containerRef}>
      <EggButton
        onClick={handleEggClick}
        whileHover={!isOpen ? { scale: 1.06, rotate: [0, -2, 2, 0], transition: { duration: 0.4, ease: "easeInOut" } } : { scale: 1.04 }}
        whileTap={{ scale: 0.94 }}
        aria-label={isOpen ? "Close socials" : "Crack open egg for social links"}
        aria-expanded={isOpen}
      >
        <EggSvg
          viewBox="0 0 80 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <radialGradient id="eggShellGrad" cx="35%" cy="30%" r="75%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="35%" stopColor="#fff1db" />
              <stop offset="70%" stopColor="#f8d6a7" />
              <stop offset="100%" stopColor="#df9a52" />
            </radialGradient>

            <radialGradient id="yolkGrad" cx="35%" cy="30%" r="65%">
              <stop offset="0%" stopColor="#fff37a" />
              <stop offset="45%" stopColor="#ffb703" />
              <stop offset="85%" stopColor="#fb8500" />
              <stop offset="100%" stopColor="#d35400" />
            </radialGradient>
          </defs>

          {/* Egg Interior: Golden Yolk & Baby Chick Peeking */}
          <motion.g
            initial={false}
            animate={isOpen ? {
              scale: 1,
              opacity: 1,
              transition: { delay: 0.04, duration: 0.28, type: 'spring', stiffness: 420, damping: 20 }
            } : {
              scale: 0.5,
              opacity: 0,
              transition: { duration: 0.15 }
            }}
            style={{ transformOrigin: "40px 48px" }}
          >
            {/* Yolk Base */}
            <circle cx="40" cy="46" r="22" fill="url(#yolkGrad)" stroke="#c0392b" strokeWidth="1" />
            
            {/* Cute Chick Eyes */}
            <circle cx="33" cy="43" r="2.4" fill="#2c3e50" />
            <circle cx="34" cy="42" r="0.9" fill="#ffffff" />
            <circle cx="47" cy="43" r="2.4" fill="#2c3e50" />
            <circle cx="48" cy="42" r="0.9" fill="#ffffff" />
            
            {/* Rosy Cheeks */}
            <ellipse cx="28" cy="47" rx="2.8" ry="1.8" fill="#ff7675" opacity="0.65" />
            <ellipse cx="52" cy="47" rx="2.8" ry="1.8" fill="#ff7675" opacity="0.65" />

            {/* Cute Orange Beak */}
            <polygon points="40,46 36,50 44,50" fill="#e67e22" stroke="#d35400" strokeWidth="0.8" />
            
            {/* Chick Hair Tuft */}
            <path d="M 39 31 Q 40 26 43 27 Q 41 29 40 31" fill="#f39c12" />

            {/* Close '✕' Badge Tag on Yolk */}
            <circle cx="56" cy="32" r="7" fill="#ff4757" stroke="#ffffff" strokeWidth="1.2" />
            <path d="M 53.5 29.5 L 58.5 34.5 M 58.5 29.5 L 53.5 34.5" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" />
          </motion.g>

          {/* Bottom Shell Half */}
          <motion.g
            animate={isOpen ? {
              y: 3,
              rotate: 1.5,
              transition: { type: "spring", stiffness: 440, damping: 22 }
            } : {
              y: 0,
              rotate: 0,
              transition: { type: "spring", stiffness: 500, damping: 25 }
            }}
            style={{ transformOrigin: "40px 85px" }}
          >
            <path
              d="M 6 46 L 18 41 L 28 50 L 40 39 L 52 51 L 64 42 L 74 46 C 74 74, 60 95, 40 95 C 20 95, 6 74, 6 46 Z"
              fill="url(#eggShellGrad)"
              stroke="#b87333"
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            
            {/* Farm Egg Speckles */}
            <circle cx="22" cy="64" r="0.9" fill="#9c5e25" opacity="0.4" />
            <circle cx="56" cy="68" r="1.1" fill="#9c5e25" opacity="0.45" />
            <circle cx="36" cy="84" r="0.8" fill="#9c5e25" opacity="0.35" />
            <circle cx="48" cy="78" r="1.0" fill="#9c5e25" opacity="0.4" />
            
            {/* Crisp "SOCIALS" Label */}
            <text
              x="40"
              y="76"
              textAnchor="middle"
              fill="#ffffff"
              fontWeight="900"
              fontSize="11"
              letterSpacing="0.8"
              style={{ fontFamily: "'Comic Sans MS', 'Comic Neue', 'Fredoka One', sans-serif" }}
              opacity="0.85"
            >
              SOCIALS
            </text>
            <text
              x="40"
              y="75"
              textAnchor="middle"
              fill="#6d390a"
              fontWeight="900"
              fontSize="11"
              letterSpacing="0.8"
              style={{ fontFamily: "'Comic Sans MS', 'Comic Neue', 'Fredoka One', sans-serif" }}
            >
              SOCIALS
            </text>
          </motion.g>

          {/* Top Shell Half */}
          <motion.g
            animate={isOpen ? {
              y: -22,
              x: -5,
              rotate: -24,
              transition: { type: "spring", stiffness: 420, damping: 20 }
            } : {
              y: 0,
              x: 0,
              rotate: 0,
              transition: { type: "spring", stiffness: 500, damping: 25 }
            }}
            style={{ transformOrigin: "15px 46px" }}
          >
            <path
              d="M 6 46 C 6 22, 20 5, 40 5 C 60 5, 74 22, 74 46 L 64 42 L 52 51 L 40 39 L 28 50 L 18 41 Z"
              fill="url(#eggShellGrad)"
              stroke="#b87333"
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            
            {/* Top Shell Speckles */}
            <circle cx="28" cy="24" r="0.9" fill="#9c5e25" opacity="0.4" />
            <circle cx="50" cy="28" r="1.1" fill="#9c5e25" opacity="0.45" />
            <circle cx="38" cy="16" r="0.8" fill="#9c5e25" opacity="0.35" />

            {/* Delicate Shell Highlight */}
            <path d="M 22 18 C 28 10, 36 9, 44 9" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
          </motion.g>
        </EggSvg>
      </EggButton>

      <AnimatePresence>
        {isOpen && (
          <SocialBubbleList
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {socialLinks.map((social, index) => (
              <div key={social.id} style={{ position: 'relative' }}>
                <SocialBubble
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={social.className}
                  onMouseEnter={() => setHoveredBubble(social.id)}
                  onMouseLeave={() => setHoveredBubble(null)}
                  whileHover={{ scale: 1.14 }}
                  whileTap={{ scale: 0.92 }}
                  initial={{ opacity: 0, y: 15, scale: 0.4 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 15, scale: 0.4 }}
                  transition={{
                    duration: 0.25,
                    delay: index * 0.04,
                    type: "spring",
                    stiffness: 380,
                    damping: 24
                  }}
                >
                  {social.svg}
                </SocialBubble>
                
                <AnimatePresence>
                  {hoveredBubble === social.id && (
                    <Tooltip
                      initial={{ opacity: 0, scale: 0.8, x: 10 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.8, x: 10 }}
                      transition={{ duration: 0.15 }}
                    >
                      {social.label}
                    </Tooltip>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </SocialBubbleList>
        )}
      </AnimatePresence>
    </SocialBubbleContainer>
  );
};

export default FloatingSocialBubbles;
